import React from 'react';
import { sortRoomsForDesignFlow } from '../../../../../utils/designWorkflow.js';
import ScheduleSelect from '../../../../Schedule/ScheduleSelect';
import CustomSpecsEditor from '../../../../shared/CustomSpecsEditor';

export default function LineItemDescription({
  it,
  updateItem,
  rooms = [],
  focusedRoomId,
  setFocusedRoomId,
  handleCreateRoom,
  wrapperClass = 'li-room-wrapper',
  readOnly = false,
  schedules = [],
  onScheduleCreated,
  userId,
  projectId,
}) {
  const updateDimension = (key, value) =>
    updateItem(it.itemId, { dimensions: { ...(it.dimensions || {}), [key]: value } });

  const specFields = (
    <>
      <div className="li-specs-row">
        <input
          className="if-input li-spec-color"
          type="text"
          placeholder="Color / Finish"
          value={it.color || ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { color: e.target.value })}
        />
        <input
          className="if-input li-spec-material"
          type="text"
          placeholder="Material"
          value={it.materials || ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { materials: e.target.value })}
        />
      </div>

      <div className="li-dimensions-row">
        <input
          className="if-input li-spec-dim"
          type="text"
          placeholder="W&quot;"
          value={it.dimensions?.width ?? ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateDimension('width', e.target.value)}
        />
        <input
          className="if-input li-spec-dim"
          type="text"
          placeholder="L&quot;"
          value={it.dimensions?.length ?? ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateDimension('length', e.target.value)}
        />
        <input
          className="if-input li-spec-dim"
          type="text"
          placeholder="H&quot;"
          value={it.dimensions?.height ?? ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateDimension('height', e.target.value)}
        />
        <input
          className="if-input li-spec-dim"
          type="text"
          placeholder="D&quot;"
          value={it.dimensions?.depth ?? ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateDimension('depth', e.target.value)}
        />
      </div>

      <CustomSpecsEditor
        specs={it.customSpecs}
        onChange={(customSpecs) => updateItem(it.itemId, { customSpecs })}
        readOnly={readOnly}
        title="Custom specs"
        inputClassName="if-input"
        compact
      />
    </>
  );

  // PO-safe short circuit
  if (!rooms.length && !handleCreateRoom) {
    return (
      <div className="li-col li-desc">
        <div className="li-title-row">
          <input
            className="li-client-name-input"
            value={it.name || ''}
            placeholder="Item name"
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { name: e.target.value })}
          />
        </div>

        <textarea
          className="if-textarea li-description"
          placeholder="Description"
          value={it.description || ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { description: e.target.value })}
        />

        <input
          className="if-input li-product-link"
          type="text"
          placeholder="Product link (optional)"
          value={it.link || ''}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { link: e.target.value })}
        />

        {specFields}
      </div>
    );
  }

  const sortedRooms = sortRoomsForDesignFlow(rooms || []);
  const filteredRooms = sortedRooms.filter((r) =>
    r.name.toLowerCase().includes((it.room || '').toLowerCase())
  );

  const exactMatch = filteredRooms.some(
    (r) => r.name.toLowerCase() === (it.room || '').toLowerCase()
  );

  const submitNewRoom = (rawName) => {
    const nextRoom = rawName.trim();
    if (!nextRoom) {
      setFocusedRoomId(null);
      return;
    }

    handleCreateRoom(nextRoom);
    setFocusedRoomId(null);
  };

  return (
    <div className="li-col li-desc">
      <div className="li-title-row">
        <input
          className="li-client-name-input"
          value={it.name || ''}
          placeholder="Product name"
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { name: e.target.value })}
        />

        {it.clientName && it.clientName !== it.name && (
          <span className="tag-client-name">Client Name</span>
        )}

        {it.updatedAt && <span className="tag-synced">Synced</span>}
      </div>

      <input
        className="if-input li-client-name-override"
        type="text"
        placeholder="Client-facing name (optional)"
        value={it.clientName || ''}
        readOnly={readOnly}
        onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { clientName: e.target.value })}
      />

      <div className={wrapperClass}>
        <select
          className="if-input li-room-select"
          value={it.room || ''}
          disabled={readOnly}
          onChange={readOnly ? undefined : (e) => {
            if (e.target.value === '__create__') {
              setFocusedRoomId(it.itemId);
            } else {
              updateItem(it.itemId, { room: e.target.value });
            }
          }}
        >
          <option value="">Select room</option>

          {!exactMatch && it.room ? <option value={it.room}>{it.room}</option> : null}

          {sortedRooms.map((r) => (
            <option key={r.sectionId || r.name} value={r.name}>
              {r.name}
            </option>
          ))}

          {!readOnly && <option value="__create__">+ Add new room…</option>}
        </select>

        {!readOnly && focusedRoomId === it.itemId && (
          <input
            className="if-input li-room-create"
            placeholder="New room name"
            autoFocus
            onBlur={(e) => submitNewRoom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitNewRoom(e.target.value);
              }
              if (e.key === 'Escape') {
                setFocusedRoomId(null);
              }
            }}
          />
        )}
      </div>

      <ScheduleSelect
        schedules={schedules}
        value={it.scheduleId}
        onChange={(v) => updateItem(it.itemId, { scheduleId: v })}
        userId={userId}
        projectId={projectId}
        onScheduleCreated={onScheduleCreated}
        selectClassName="if-input li-schedule-select"
        placeholder="Select Schedule"
        disabled={readOnly}
      />

      <textarea
        className="if-textarea li-description"
        placeholder="Client-facing description"
        value={it.description || ''}
        readOnly={readOnly}
        onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { description: e.target.value })}
      />

      <input
        className="if-input li-product-link"
        type="text"
        placeholder="Product link (optional)"
        value={it.link || ''}
        readOnly={readOnly}
        onChange={readOnly ? undefined : (e) => updateItem(it.itemId, { link: e.target.value })}
      />

      {specFields}
    </div>
  );
}
